import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import {
  usersApi,
  type UserProfile,
  type UpdateUserRequest,
} from "@/api/users";
import { useAuthStore } from "@/shared/model/auth-store";
import { QUERY_KEYS } from "@/hooks/query-keys";
import { QUERY_SPECIFIC_CONFIG } from "@/shared/config";
import { handleError } from "@/shared/api/error-handler";

export function useMe() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  return useQuery<UserProfile>({
    queryKey: QUERY_KEYS.user("me"),
    queryFn: () => usersApi.fetchMe(),
    staleTime: QUERY_SPECIFIC_CONFIG.USER.staleTime,
    gcTime: QUERY_SPECIFIC_CONFIG.USER.gcTime,
    enabled: isAuthenticated,
  });
}

export function useUser(userId: string) {
  return useQuery<UserProfile>({
    queryKey: QUERY_KEYS.user(userId),
    queryFn: () => usersApi.fetchById(userId),
    enabled: Boolean(userId),
    staleTime: QUERY_SPECIFIC_CONFIG.USER.staleTime,
    gcTime: QUERY_SPECIFIC_CONFIG.USER.gcTime,
  });
}

export function useUpdateMe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateUserRequest) => usersApi.updateMe(data),
    onSuccess: (updated) => {
      qc.setQueryData(QUERY_KEYS.user("me"), updated);
      qc.invalidateQueries({ queryKey: QUERY_KEYS.user("me") });
    },
    onError: (error) => {
      handleError(error, { component: "useUpdateMe", action: "updateMe" });
    },
  });
}
